import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Database } from '@/types/supabase'
import { logger } from '@/lib/services/logger-service'
import { useAuthStore } from '@/lib/store/auth'

export type SpecialistInfo = Database['public']['CompositeTypes']['specialist_info_type']

export type CaregiverSpecialist = {
  id: string;
  name: string;
  email: string;
  isActive: boolean;
  roomId: string | null;
  conversationId: string | null;
  days_left: number | null;
  agreement_expired: boolean;
}

/**
 * Transforms specialist info from the caregiver profile into a frontend-friendly format
 * and calculates agreement-related fields
 */
function processSpecialist(specialistInfo: SpecialistInfo): CaregiverSpecialist {
  // Calculate days left and agreement expired status
  let days_left = null;
  let agreement_expired = false;

  if (specialistInfo.agreement_date) {
    const endDate = new Date(specialistInfo.agreement_date);
    const now = new Date();
    
    // Set both dates to midnight for accurate day calculation
    const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endDateMidnight = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    
    // Calculate days left (including today)
    const diffTime = endDateMidnight.getTime() - nowDate.getTime();
    days_left = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    agreement_expired = days_left <= 0;
  }

  return {
    id: specialistInfo.specialist_id || '',
    name: specialistInfo.specialist_name || 'Sleep Specialist',
    email: specialistInfo.specialist_email || '',
    isActive: specialistInfo.is_active_specialist || false,
    roomId: specialistInfo.room_id || null,
    conversationId: specialistInfo.conversationid || null,
    days_left,
    agreement_expired,
  }
}

interface UseCaregiverSpecialistsOptions {
  enabled?: boolean;
  caregiverId?: string;
  caregiverAuthId?: string;
}

/**
 * Hook for fetching specialists assigned to a caregiver using getcaregiverprofiledatav2
 */
export function useCaregiverSpecialists(options: UseCaregiverSpecialistsOptions = {}) {
  const { user } = useAuthStore();
  const caregiverId = options.caregiverId;
  const caregiverAuthId = options.caregiverAuthId || user?.id;
  const { enabled = true } = options;
  
  return useQuery<CaregiverSpecialist[]>({
    queryKey: ['caregiver-specialists', caregiverAuthId, caregiverId],
    queryFn: async () => {
      if (!caregiverAuthId && !caregiverId) {
        return [];
      }
      
      try {
        logger.debug('Fetching caregiver specialists', {
          caregiverAuthId,
          caregiverId
        });
        
        const params: Record<string, any> = {};
        
        if (caregiverId) {
          params.p_caregiver_id = caregiverId;
        }
        
        if (caregiverAuthId) {
          params.p_caregiver_auth_user_id = caregiverAuthId;
        }
        
        const { data, error } = await supabase
          .rpc('getcaregiverprofiledatav2', params);
          
        if (error) {
          throw new Error(error.message);
        }
        
        if (!data || data.length === 0 || !data[0]?.specialists) {
          return [];
        }
        
        // Extract specialists array from the response
        const specialists = data[0].specialists || [];
        return specialists.map(processSpecialist);
      } catch (error) {
        logger.error('Error fetching caregiver specialists', { error });
        throw error;
      }
    },
    enabled: enabled && !!(caregiverAuthId || caregiverId),
    staleTime: 5 * 60 * 1000, // 5 minutes 
    refetchOnWindowFocus: false
  });
}
